import { useCallback, useEffect, useRef, useState } from 'react'
import { ArrowLeft, Pause, Play, RotateCcw, RotateCw, Square, TriangleAlert } from 'lucide-react'
import { isRemotePlayerCommand, type RemotePlayerCommand } from '../lib/remoteProtocol'

interface Props {
  youtubeId?: string
  directUrl?: string
  title: string
  onClose: () => void
}

export function TrailerOverlay({ youtubeId, directUrl, title, onClose }: Props) {
  const videoRef = useRef<HTMLVideoElement>(null)
  const frameRef = useRef<HTMLIFrameElement>(null)
  const [playing, setPlaying] = useState(true)
  // Position is only ever read to compute the next relative seek, never
  // rendered. A ref also keeps the seek command out of a state updater, which
  // StrictMode invokes twice.
  const youtubeTime = useRef(0)
  const [unavailable, setUnavailable] = useState(false)
  const embedResponded = useRef(false)

  const youtubeCommand = useCallback((func: 'playVideo' | 'pauseVideo' | 'stopVideo' | 'seekTo', args: unknown[] = []) => {
    frameRef.current?.contentWindow?.postMessage(JSON.stringify({ event: 'command', func, args }), 'https://www.youtube-nocookie.com')
  }, [])

  useEffect(() => {
    const message = (event: MessageEvent) => {
      if (event.origin !== 'https://www.youtube-nocookie.com' && event.origin !== 'https://www.youtube.com') return
      let payload: unknown = event.data
      try { if (typeof payload === 'string') payload = JSON.parse(payload) } catch { return }
      if (!payload || typeof payload !== 'object') return
      const value = payload as { event?: string; info?: { currentTime?: unknown; playerState?: unknown } }
      if (value.event !== 'infoDelivery' || !value.info) return
      embedResponded.current = true
      setUnavailable(false)
      if (typeof value.info.currentTime === 'number' && Number.isFinite(value.info.currentTime)) youtubeTime.current = Math.max(0, value.info.currentTime)
      if (value.info.playerState === 1) setPlaying(true)
      else if (value.info.playerState === 0 || value.info.playerState === 2) setPlaying(false)
    }
    window.addEventListener('message', message)
    return () => window.removeEventListener('message', message)
  }, [])

  // A sideloaded app runs from file://, where the embed can be refused outright.
  // Without this the viewer gets a black rectangle and a control bar posting
  // messages into a frame that never listens.
  useEffect(() => {
    if (!youtubeId) return
    embedResponded.current = false
    // Only a frame that has never answered is a dead one. The player keeps
    // sending progress messages while it plays, so testing the flag rather than
    // firing unconditionally is what stops this showing over a working trailer.
    const timer = window.setTimeout(() => {
      if (!embedResponded.current) setUnavailable(true)
    }, 6_000)
    return () => clearTimeout(timer)
  }, [youtubeId])

  const applyCommand = useCallback((action: RemotePlayerCommand) => {
    const video = videoRef.current
    if (action.kind === 'stop') {
      video?.pause()
      youtubeCommand('stopVideo')
      onClose()
      return
    }
    if (action.kind === 'play') {
      video?.play().catch(() => undefined)
      youtubeCommand('playVideo')
      setPlaying(true)
      return
    }
    if (action.kind === 'pause') {
      video?.pause()
      youtubeCommand('pauseVideo')
      setPlaying(false)
      return
    }
    if (action.kind === 'toggle') {
      if (video) {
        const willPlay = video.paused
        if (willPlay) video.play().catch(() => undefined)
        else video.pause()
        setPlaying(willPlay)
      } else {
        youtubeCommand(playing ? 'pauseVideo' : 'playVideo')
        setPlaying((value) => !value)
      }
      return
    }
    if (action.kind === 'seek_by') {
      if (video) video.currentTime = Math.max(0, Math.min(video.duration || Infinity, video.currentTime + action.seconds))
      else {
        const target = Math.max(0, youtubeTime.current + action.seconds)
        youtubeTime.current = target
        youtubeCommand('seekTo', [target, true])
      }
      return
    }
    if (action.kind === 'seek_to') {
      if (video) video.currentTime = Math.max(0, Math.min(video.duration || action.seconds, action.seconds))
      else {
        youtubeCommand('seekTo', [action.seconds, true])
        youtubeTime.current = action.seconds
      }
    }
  }, [onClose, playing, youtubeCommand])

  useEffect(() => {
    const command = (event: Event) => {
      const action = (event as CustomEvent<unknown>).detail
      if (isRemotePlayerCommand(action)) applyCommand(action)
    }
    const key = (event: KeyboardEvent) => {
      const code = event.keyCode || event.which
      const stop = () => { event.preventDefault(); event.stopImmediatePropagation() }
      if (code === 413) { stop(); applyCommand({ kind: 'stop' }) }
      else if (code === 415) { stop(); applyCommand({ kind: 'play' }) }
      else if (code === 19) { stop(); applyCommand({ kind: 'pause' }) }
      else if (code === 412) { stop(); applyCommand({ kind: 'seek_by', seconds: -30 }) }
      else if (code === 417) { stop(); applyCommand({ kind: 'seek_by', seconds: 30 }) }
    }
    window.addEventListener('vela:player-command', command)
    window.addEventListener('keydown', key, true)
    return () => {
      window.removeEventListener('vela:player-command', command)
      window.removeEventListener('keydown', key, true)
    }
  }, [applyCommand])

  return (
    <div className="trailer-overlay" role="dialog" aria-label={`${title} trailer`}>
      <div className="trailer-top">
        <button className="round-button" data-focusable data-autofocus onClick={onClose} aria-label="Close trailer"><ArrowLeft size="2rem" /></button>
        <div><small>TRAILER</small><strong>{title}</strong></div>
      </div>
      {directUrl ? (
        <video ref={videoRef} src={directUrl} title={`${title} trailer`} autoPlay playsInline onPlay={() => setPlaying(true)} onPause={() => setPlaying(false)} onError={() => setUnavailable(true)} />
      ) : youtubeId ? (
        <iframe
          ref={frameRef}
          src={`https://www.youtube-nocookie.com/embed/${encodeURIComponent(youtubeId)}?autoplay=1&controls=1&rel=0&enablejsapi=1`}
          title={`${title} trailer`}
          allow="autoplay; encrypted-media; picture-in-picture"
          allowFullScreen
          onLoad={() => frameRef.current?.contentWindow?.postMessage(JSON.stringify({ event: 'listening', id: 'vela-trailer' }), 'https://www.youtube-nocookie.com')}
        />
      ) : null}
      {unavailable ? (
        <div className="trailer-unavailable">
          <TriangleAlert size="2.75rem" />
          <h2>This trailer will not play here</h2>
          <p>The provider refused the request. Press BACK to return to the title.</p>
          <button className="secondary-button" data-focusable onClick={onClose}>Back to the title</button>
        </div>
      ) : null}
      <div className="trailer-controls" data-nav-group="row" data-nav-key="trailer-controls">
        <button className="round-button" data-focusable aria-label="Back 30 seconds" onClick={() => applyCommand({ kind: 'seek_by', seconds: -30 })}><RotateCcw size="1.75rem" /><small>30</small></button>
        <button className="round-button trailer-play" data-focusable aria-label={playing ? 'Pause trailer' : 'Play trailer'} onClick={() => applyCommand({ kind: 'toggle' })}>{playing ? <Pause size="2rem" fill="currentColor" /> : <Play size="2rem" fill="currentColor" />}</button>
        <button className="round-button" data-focusable aria-label="Forward 30 seconds" onClick={() => applyCommand({ kind: 'seek_by', seconds: 30 })}><RotateCw size="1.75rem" /><small>30</small></button>
        <button className="round-button" data-focusable aria-label="Stop trailer" onClick={() => applyCommand({ kind: 'stop' })}><Square size="1.5rem" fill="currentColor" /></button>
      </div>
    </div>
  )
}
